/**
 * <wc-cart-bar>: the sticky mobile cart bar's behaviour (session W5; plan
 * section 3.4; snippets/wc-cart-bar draws it, assets/wc-cart-bar.css styles
 * it). Loaded from snippets/wc-body-end-w5.
 *
 * It SHOWS when every one of these is true, and says which one is not in
 * `data-hidden-because` (for W-C, and for anyone debugging on a phone):
 *   narrow       the viewport is under 750 px
 *   portrait     the cart holds a portrait line (the server's data-portraits)
 *   drawer       the cart drawer is closed
 *   brief        the brief is not in focus mode (W4's html.wc-brief-open)
 *   chat         ChatBase's chat window is closed (W1 loads ChatBase; on a
 *                phone its window can fill the screen, and lifting it above
 *                the bar would push its header and close button off the top)
 *   clear        nothing it must not cover is under it: an add-to-cart
 *                button, Horizon's sticky add-to-cart bar, or anything a
 *                session marks [data-wc-cart-bar-avoid]
 * /cart does not render the bar at all, and checkout is not this theme.
 *
 * Its CONTENT is re-rendered by the server with the drawer (it is a
 * data-hydration-key target inside the drawer's section), so this file never
 * builds markup. It watches the content's attributes and re-decides.
 *
 * While shown it sets html[data-wc-cart-bar="shown"] and --wc-cart-bar-h,
 * which pad the page's end and lift ChatBase's launcher above the bar. The
 * chat WINDOW is never lifted: the bar steps aside while it is open.
 */

/**
 * THE RULE, as one pure function so an offline test can hold it
 * (bin/test-wc-w5.mjs). Every reason the bar is hidden, in a fixed order; an
 * empty list means shown.
 *
 * `measuring` is true only on a page's first paint, until the observer has
 * said once what is under the bar: showing before that answer could paint the
 * bar for a frame and then slide it away on load, which is motion nobody
 * caused.
 *
 * @param {{narrow: boolean, portraits: number, drawerOpen: boolean, briefOpen: boolean, chatOpen?: boolean, covering: number, measuring?: boolean}} state
 * @returns {string[]}
 */
export function hiddenBecause({
  narrow,
  portraits,
  drawerOpen,
  briefOpen,
  chatOpen = false,
  covering,
  measuring = false,
}) {
  const reasons = [];
  if (!narrow) reasons.push("wide");
  if (!(portraits > 0)) reasons.push("no-portrait");
  if (drawerOpen) reasons.push("drawer-open");
  if (briefOpen) reasons.push("brief-focus");
  if (chatOpen) reasons.push("chat-open");
  if (covering > 0) reasons.push("covering");
  if (measuring) reasons.push("measuring");
  return reasons;
}

const NARROW = globalThis.matchMedia?.("(max-width: 749px)");

/**
 * What the bar must never sit on top of: the page's OWN add-to-cart (Horizon's
 * product form, its sticky add-to-cart bar, the brief's Add), and anything a
 * session marks [data-wc-cart-bar-avoid]. Checked against the bar's own band.
 *
 * A product CARD's quick-add is deliberately not on the list. It is also a
 * button[name="add"], and a grid of them scrolls through the band all the
 * time, so avoiding them would make the bar flicker in and out on every
 * collection page; a card scrolls out from under the bar like any other
 * content. (Portrait cards have no quick-add at all: selling-plan products
 * route to the product page.)
 */
const AVOID = [
  'button[name="add"]',
  ".add-to-cart-button",
  ".sticky-add-to-cart__bar",
  "[data-wc-cart-bar-avoid]",
].join(",");

/** The containers whose add buttons are a card's quick-add, not the page's. */
const NOT_THE_PAGES_OWN = "quick-add-component, .quick-add, .product-card, product-card, .quick-add-modal";

/** W4's focus-mode signal (docs/wc-brief.md on f3): a class on <html>. */
const BRIEF_OPEN_CLASS = "wc-brief-open";

/**
 * ChatBase's chat window. ChatBase shows and hides it by its display, which is
 * also what W1's chat opener (snippets/wc-head-w1) reads to decide whether to
 * click the launcher.
 */
const CHAT_WINDOW_ID = "chatbase-bubble-window";

if (typeof HTMLElement !== "undefined" && !customElements.get("wc-cart-bar")) {
  class WcCartBar extends HTMLElement {
    /** @type {Set<Element>} */
    #covered = new Set();
    /** @type {IntersectionObserver | null} */
    #io = null;
    /** @type {Set<Element>} */
    #watched = new Set();
    #band = 0;
    /** True until the observer's first report on this page (see hiddenBecause). */
    #measuring = true;
    #settling = false;
    #rescanTimer = 0;
    #abort = new AbortController();
    /** @type {HTMLElement | null} */
    #chatWindow = null;
    /** @type {MutationObserver | null} */
    #chatWatch = null;

    connectedCallback() {
      const { signal } = this.#abort;

      // No slide on the page's first paint: the bar was already there on the
      // page before. Motion is for a change the shopper caused.
      this.toggleAttribute("data-instant", true);

      this.addEventListener("click", this.#onClick, { signal });
      this.addEventListener("keydown", this.#onKeydown, { signal });
      document.addEventListener("pointerdown", this.#onPointerDown, { signal, capture: true });
      NARROW?.addEventListener("change", () => this.#rebuild(), { signal });
      window.addEventListener("resize", () => this.#rebuild(), { signal, passive: true });
      document.addEventListener("theme-drawer:open", () => this.#evaluate(), { signal });
      document.addEventListener("theme-drawer:close", () => this.#evaluate(), { signal });
      document.addEventListener("shopify:cart:lines-update", () => this.#scheduleRescan(), { signal });
      document.addEventListener("shopify:section:load", () => this.#scheduleRescan(), { signal });
      window.addEventListener("pageshow", () => this.#rebuild(), { signal });

      // The server's content changes under us (a morph after every cart
      // change); W4's focus mode is a class on <html>.
      const contentObserver = new MutationObserver(() => {
        this.#evaluate();
        this.#scheduleRescan();
      });
      contentObserver.observe(this, {
        subtree: true,
        attributes: true,
        attributeFilter: ["data-portraits"],
        childList: true,
      });
      const rootObserver = new MutationObserver(() => this.#evaluate());
      rootObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
      // ChatBase adds its window after the first interaction (W1 loads it
      // late), so watch for it to appear, then watch it open and close.
      const chatFinder = new MutationObserver(() => this.#findChat());
      chatFinder.observe(document.body, { childList: true, subtree: true });
      signal.addEventListener("abort", () => {
        contentObserver.disconnect();
        rootObserver.disconnect();
        chatFinder.disconnect();
        this.#chatWatch?.disconnect();
      });
      this.#findChat();

      // A surface's size changes when the consent line opens or the text wraps.
      const resizeObserver = new ResizeObserver(() => this.#measure());
      resizeObserver.observe(this);
      signal.addEventListener("abort", () => resizeObserver.disconnect());

      // iOS Safari applies :active (the press feedback) only where a touch
      // listener exists.
      this.addEventListener("touchstart", () => {}, { passive: true, signal });

      this.#rebuild();
    }

    disconnectedCallback() {
      this.#abort.abort();
      this.#io?.disconnect();
      window.clearTimeout(this.#rescanTimer);
      document.documentElement.removeAttribute("data-wc-cart-bar");
    }

    /** @returns {HTMLElement | null} */
    get #content() {
      return this.querySelector(".wc-cart-bar__content");
    }

    /** @returns {HTMLElement | null} */
    get #drawer() {
      return document.getElementById("cart-drawer");
    }

    /** Tracks ChatBase's window as it is added, replaced or removed. Cheap: one id lookup per batch of DOM changes. */
    #findChat() {
      const found = document.getElementById(CHAT_WINDOW_ID);
      if (found === this.#chatWindow) return;
      this.#chatWatch?.disconnect();
      this.#chatWatch = null;
      this.#chatWindow = found;
      if (found) {
        this.#chatWatch = new MutationObserver(() => this.#evaluate());
        this.#chatWatch.observe(found, { attributes: true, attributeFilter: ["style", "class", "hidden"] });
      }
      this.#evaluate();
    }

    /**
     * Open means a shopper can SEE it: displayed, visible, not transparent, with
     * a size, and on screen. Every other way a widget can park a closed window
     * reads as closed, because the cost of a false "open" is a bar that never
     * shows once ChatBase has loaded.
     * @returns {boolean}
     */
    #chatOpen() {
      const w = this.#chatWindow;
      if (!w || !w.isConnected) return false;
      const style = getComputedStyle(w);
      if (style.display === "none" || style.visibility === "hidden" || Number(style.opacity) === 0) return false;
      const r = w.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight;
    }

    /** @param {MouseEvent} event */
    #onClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest("[data-wc-cart-bar-open]")) {
        const drawer = /** @type {any} */ (this.#drawer);
        if (drawer && typeof drawer.open === "function") {
          drawer.open();
        } else {
          window.location.href = window.Theme?.routes?.cart_url ?? "/cart";
        }
        return;
      }

      if (target.closest("[data-wc-cart-bar-terms-close]")) {
        this.#setTerms(false, true);
        return;
      }

      const toggle = target.closest("[data-wc-cart-bar-consent]");
      if (toggle instanceof HTMLButtonElement) {
        this.#setTerms(toggle.getAttribute("aria-expanded") !== "true", false);
      }
    };

    /**
     * THE $0 TERMS (Luke, L-32, "option b"): "Terms" opens the consent
     * sentence above the row. Opening moves focus to the sentence so a screen
     * reader reads it; closing by Close or Esc returns focus to "Terms" (a tap
     * on "Terms" itself leaves it there; a tap elsewhere moves nothing).
     * @param {boolean} open
     * @param {boolean} returnFocus
     */
    #setTerms(open, returnFocus) {
      const toggle = this.querySelector("[data-wc-cart-bar-consent]");
      const panel = this.querySelector("[data-wc-cart-bar-terms]");
      if (!(toggle instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;
      if ((toggle.getAttribute("aria-expanded") === "true") === open) return;
      toggle.setAttribute("aria-expanded", String(open));
      panel.hidden = !open;
      if (open) {
        panel.querySelector("#WcCartBarConsent")?.focus({ preventScroll: true });
      } else if (returnFocus || panel.contains(document.activeElement)) {
        toggle.focus({ preventScroll: true });
      }
    }

    get #termsOpen() {
      return this.querySelector("[data-wc-cart-bar-consent]")?.getAttribute("aria-expanded") === "true";
    }

    /** @param {KeyboardEvent} event */
    #onKeydown = (event) => {
      if (event.key === "Escape" && this.#termsOpen) {
        event.preventDefault();
        this.#setTerms(false, true);
      }
    };

    /** A tap anywhere outside the panel and "Terms" closes it, moving nothing. @param {PointerEvent} event */
    #onPointerDown = (event) => {
      if (!this.#termsOpen) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest("[data-wc-cart-bar-terms], [data-wc-cart-bar-consent]")) return;
      this.#setTerms(false, false);
    };

    /** Re-measures the band and re-observes everything it must not cover. */
    #rebuild() {
      this.#measure();
      this.#rescan(true);
      this.#evaluate();
    }

    #scheduleRescan() {
      window.clearTimeout(this.#rescanTimer);
      this.#rescanTimer = window.setTimeout(() => this.#rescan(false), 150);
    }

    /**
     * The band is the strip at the bottom of the viewport the bar occupies when
     * shown. Measured from the surface when it has a size, else its minimum
     * (76 px since A26; the $0 terms are a button in the row since L-32), so the answer
     * to "would it cover something?" never depends on whether it is currently
     * showing. An OPEN terms panel is not counted: it is a moment's reading,
     * and counting it could hide the bar under the shopper's eyes.
     */
    #measure() {
      const surface = this.querySelector(".wc-cart-bar__surface");
      const terms = this.querySelector("[data-wc-cart-bar-terms]");
      const termsHeight = terms instanceof HTMLElement && !terms.hidden ? terms.getBoundingClientRect().height : 0;
      const floor = 76;
      const full = surface ? Math.ceil(surface.getBoundingClientRect().height) : 0;
      // The page's padding and ChatBase's lift follow the WHOLE surface, so an
      // open terms panel is never under the launcher...
      document.documentElement.style.setProperty("--wc-cart-bar-h", `${Math.max(full, floor)}px`);
      // ...but the band it avoids covering things with is the row alone.
      const band = Math.max(full - Math.ceil(termsHeight), floor);
      if (Math.abs(band - this.#band) > 2) {
        this.#band = band;
        this.#rescan(true);
      }
    }

    /**
     * @param {boolean} force - Rebuild the observer even if the targets have not changed
     */
    #rescan(force) {
      const targets = new Set(
        Array.from(document.querySelectorAll(AVOID)).filter(
          (el) => !this.contains(el) && (el.matches("[data-wc-cart-bar-avoid]") || !el.closest(NOT_THE_PAGES_OWN)),
        ),
      );
      const same = targets.size === this.#watched.size && [...targets].every((el) => this.#watched.has(el));
      if (same && !force && this.#io) return;

      this.#io?.disconnect();
      // Keep what is known to be under the bar until the new observer's first
      // report replaces it: clearing it here would show the bar for a frame
      // on every resize and cart change, then slide it away again.
      this.#covered = new Set([...this.#covered].filter((el) => targets.has(el)));
      this.#watched = targets;
      if (!NARROW?.matches || targets.size === 0) {
        this.#io = null;
        this.#measuring = false;
        this.#evaluate();
        return;
      }

      // The root is shrunk to the bottom band: anything intersecting it is
      // something the bar would sit on.
      const top = Math.max(0, window.innerHeight - (this.#band || 76));
      this.#io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const visible = entry.isIntersecting && !this.#isHidden(entry.target);
            if (visible) this.#covered.add(entry.target);
            else this.#covered.delete(entry.target);
          }
          this.#measuring = false;
          this.#evaluate();
        },
        { rootMargin: `-${top}px 0px 0px 0px`, threshold: 0 },
      );
      for (const el of targets) this.#io.observe(el);
    }

    /**
     * An observer reports geometry, not paint. A box that is laid out but
     * `visibility: hidden` covers nothing a shopper can press. (Opacity is NOT
     * read: Horizon's sticky add-to-cart bar crosses into the band while it
     * fades in, and a check made at that instant would miss it for good.)
     *
     * @param {Element} el
     */
    #isHidden(el) {
      return getComputedStyle(el).visibility === "hidden";
    }

    #evaluate() {
      // Drop anything the page removed since it was observed.
      for (const el of this.#covered) if (!el.isConnected) this.#covered.delete(el);
      const reasons = hiddenBecause({
        narrow: Boolean(NARROW?.matches),
        portraits: Number(this.#content?.dataset.portraits ?? 0),
        drawerOpen: Boolean(this.#drawer?.hasAttribute("open")),
        briefOpen: document.documentElement.classList.contains(BRIEF_OPEN_CLASS),
        chatOpen: this.#chatOpen(),
        covering: this.#covered.size,
        measuring: this.#measuring,
      });

      const shown = reasons.length === 0;
      const state = shown ? "shown" : "hidden";
      if (this.dataset.state !== state) {
        this.dataset.state = state;
        if (!shown) this.#setTerms(false, false);
        this.inert = !shown;
        document.documentElement.setAttribute("data-wc-cart-bar", state);
        if (shown) this.#measure();
      }
      this.dataset.hiddenBecause = reasons.join(" ");

      // The first answer is painted without motion; motion is for what
      // changes after it.
      if (!this.#measuring && this.hasAttribute("data-instant") && !this.#settling) {
        this.#settling = true;
        requestAnimationFrame(() => requestAnimationFrame(() => this.removeAttribute("data-instant")));
      }
    }
  }

  customElements.define("wc-cart-bar", WcCartBar);
}
